import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { CustomConfirmDialog } from '../../../../../../../components/dialogs'
import { useToastContext } from '../../../../../../../contexts'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  requestDeletePackagingMaterial,
  requestDeletePackagingMaterials,
  usePackagingMaterialById,
} from '../../../data'

export const DeletePackagingData: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToastContext()
  const { packagingIds } = useParams()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const packagingIdsToDelete: string[] = packagingIds?.split(',') || []
  const packagingId =
    packagingIdsToDelete.length === 1 ? packagingIdsToDelete[0] : undefined

  const { data: packaging } = usePackagingMaterialById(packagingId)

  const handleDelete = async () => {
    try {
      setIsRequestRunning(true)

      await submitDelete()

      showToast({
        severity: 'success',
        summary: t(
          'masterDataManagement.packagingData.table.delete.dialog.delete.messages.success.summary',
        ),
        detail: t(
          'masterDataManagement.packagingData.table.delete.dialog.delete.messages.success.detail',
          {
            count: packagingIdsToDelete?.length,
            description: packaging?.Description,
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'masterDataManagement.packagingData.table.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitDelete = async () => {
    if (packagingIdsToDelete.length === 1) {
      await requestDeletePackagingMaterial(packagingIdsToDelete[0])
    } else {
      await requestDeletePackagingMaterials(packagingIdsToDelete)
    }
  }

  const handleClose = () => {
    navigate('/master-data-management/packaging-data')
  }

  return (
    <CustomConfirmDialog
      severity="danger"
      isVisible
      header={t(
        'masterDataManagement.packagingData.table.delete.dialog.title',
        {
          count: packagingIdsToDelete?.length,
        },
      )}
      message={t(
        'masterDataManagement.packagingData.table.delete.dialog.message',
        {
          count: packagingIdsToDelete?.length,
          description: packaging?.Description,
        },
      )}
      onHide={handleClose}
      onReject={handleClose}
      rejectLabel={t(
        'masterDataManagement.packagingData.table.delete.dialog.cancel.label',
      )}
      onAccept={handleDelete}
      acceptLabel={t(
        'masterDataManagement.packagingData.table.delete.dialog.delete.label',
      )}
    />
  )
}
